import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formSchema = z.object({
  name: z.string().min(2, "Il nome deve avere almeno 2 caratteri"),
  email: z.string().email("Inserisci un'email valida"),
  phone: z.string().min(10, "Inserisci un numero di telefono valido"),
  service: z.string().min(1, "Seleziona un servizio"),
  message: z.string().optional(),
});

const QuoteSection = ({ services }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      service: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/quote-request`, data);
      toast.success("Richiesta inviata!", {
        description: "Ti contatteremo al più presto per il preventivo.",
      });
      form.reset();
    } catch (error) {
      toast.error("Errore nell'invio", {
        description: "Si prega di riprovare più tardi.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="preventivo" className="py-24 bg-[#f8f9fa]" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <span className="text-[#5abb3d] font-semibold tracking-[0.2em] uppercase text-sm">
              Preventivo Gratuito
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-[#29383d] mt-4 tracking-tight">
              Richiedi un Preventivo
              <br />
              <span className="text-[#5abb3d]">Senza Impegno</span>
            </h2>
            <p className="text-[#394b52] mt-6 leading-relaxed text-lg">
              Compila il modulo e ti contatteremo entro 24 ore con un preventivo 
              personalizzato per le tue esigenze.
            </p>

            {/* Benefits */}
            <div className="mt-8 space-y-4">
              {[
                "Sopralluogo gratuito",
                "Preventivo dettagliato senza impegno",
                "Consulenza tecnica personalizzata",
                "Risposta entro 24 ore"
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#5abb3d] rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[#29383d]">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-[#e5e7eb]">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="quote-form">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#29383d]">Nome e Cognome *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Mario Rossi"
                          className="border-[#e5e7eb] focus:ring-[#5abb3d] focus:border-[#5abb3d]"
                          data-testid="quote-name-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#29383d]">Email *</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="mario@email.com"
                            className="border-[#e5e7eb] focus:ring-[#5abb3d] focus:border-[#5abb3d]"
                            data-testid="quote-email-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#29383d]">Telefono *</FormLabel>
                        <FormControl>
                          <Input
                            type="tel"
                            placeholder="+39 333 123 4567"
                            className="border-[#e5e7eb] focus:ring-[#5abb3d] focus:border-[#5abb3d]"
                            data-testid="quote-phone-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="service"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#29383d]">Servizio Richiesto *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger 
                            className="border-[#e5e7eb] focus:ring-[#5abb3d]"
                            data-testid="quote-service-select"
                          >
                            <SelectValue placeholder="Seleziona un servizio" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.id} data-testid={`quote-service-option-${service.id}`}>
                              {service.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#29383d]">Messaggio (opzionale)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Descrivi le tue esigenze..."
                          className="border-[#e5e7eb] focus:ring-[#5abb3d] focus:border-[#5abb3d] min-h-[100px]"
                          data-testid="quote-message-textarea"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#5abb3d] hover:bg-[#4a9a32] text-white rounded-xl py-6 text-lg font-semibold transition-all hover:-translate-y-1 hover:shadow-lg"
                  data-testid="quote-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Invia Richiesta
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteSection;
